'use client';

import { generateUrl } from '@/util/imageUtils';
import clsx from 'clsx';

import { Popover, PopoverAnchor, PopoverArrow, PopoverContent } from '@/components/ui/popover';
import { websiteEnv } from '@/constants/website-env';
import { cn } from '@/lib/utils';
import React, { useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

interface Aggregation {
  label: string;
  count: number;
  attribute_code: string;
  options: Option[];
  position?: number;
}

interface Option {
  label: string;
  value: string;
  swatch_data?: SwatchData;
  count: number;
}

interface SwatchData {
  value: string;
  thumbnail?: string;
}

interface AggregationRoot {
  code: string;
  option_id: string;
  rewrite: string;
  label?: string;
  swatch_data: SwatchData;
}

const FilterDialogColorsFamilyFilter = ({
  colorsFamilyOptions,
  aggregations,
  filterState,
  onSetFilterState,
}: {
  colorsFamilyOptions: Aggregation['options'];
  aggregations: AggregationRoot[];
  filterState: FilterState;
  onSetFilterState: React.Dispatch<React.SetStateAction<FilterState>>;
}) => {
  const prevStateIndex = filterState.findIndex(x => x.attributeCode === 'custom_option_color_ids');
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [popoverPortalContainer, setPopoverPortalContainer] = useState<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [visiblePopoverIndex, setVisiblePopoverIndex] = useState(-1);
  const selectedColorRewriteMap =
    prevStateIndex === -1
      ? {}
      : (filterState[prevStateIndex].options || []).reduce<Record<string, true>>((acc, cur) => {
          const rewrite = cur.rewrite;

          if (rewrite) {
            acc[rewrite] = true;
          }

          return acc;
        }, {});
  const colorAggregationIdMap = useMemo(
    () =>
      aggregations.reduce<Record<string, AggregationRoot>>((acc, cur) => {
        if (cur.code !== 'color') {
          return acc;
        }

        acc[cur.option_id] = cur;

        return acc;
      }, {}),
    [aggregations],
  );

  const handleOpenPopover = (e: React.MouseEvent<HTMLElement, MouseEvent>, index: number, popoverContentId: string) => {
    if (container) {
      const { offsetTop: targetOffsetTop, offsetHeight: targetOffsetHeight } = e.currentTarget;
      const containerOffsetHeight = container.offsetHeight;
      const availableHeight = containerOffsetHeight - targetOffsetTop - targetOffsetHeight;

      flushSync(() => {
        setVisiblePopoverIndex(index);
      });

      const popoverContentHeight = document.getElementById(popoverContentId)?.offsetHeight;

      if (popoverContentHeight && availableHeight < popoverContentHeight) {
        container.setAttribute(
          'style',
          `height:${targetOffsetTop + popoverContentHeight + targetOffsetHeight + 30}px;`,
        );
      } else {
        container.removeAttribute('style');
      }

      const scrollContainer = scrollContainerRef.current;

      if (scrollContainer) {
        scrollContainerRef.current?.scrollTo({
          left: 0,
          top: scrollContainer.scrollHeight,
          behavior: 'smooth',
        });
      }
    } else {
      setVisiblePopoverIndex(index);
    }
  };

  const handleClosePopover = (index: number) => {
    setVisiblePopoverIndex(x => (x !== index ? x : -1));
    container?.removeAttribute('style');
    scrollContainerRef.current?.scrollTo({
      left: 0,
      top: 0,
      behavior: 'smooth',
    });
  };

  const handleSelect = (filterItems: { value: string; label: string; rewrite: string }[], prevActive: boolean) => {
    if (prevStateIndex === -1) {
      if (filterItems.length === 0) return;

      onSetFilterState(filterState.concat({ attributeCode: 'custom_option_color_ids', options: filterItems }));
      return;
    }

    const prevOptions = filterState[prevStateIndex]?.options || [];
    const prevFilterValueMap = prevOptions.reduce<Record<string, 1>>((acc, cur) => {
      acc[cur.value] = 1;
      return acc;
    }, {});
    const filterItemValueMap = filterItems.reduce<Record<string, 1>>((acc, cur) => {
      acc[cur.value] = 1;
      return acc;
    }, {});

    const nextOptions = prevActive
      ? prevOptions.filter(x => filterItemValueMap[x.value] !== 1)
      : prevOptions.concat(filterItems.filter(filterItem => prevFilterValueMap[filterItem.value] !== 1));

    if (nextOptions.length === 0) {
      onSetFilterState(filterState.filter(x => x.attributeCode !== 'custom_option_color_ids'));
      return;
    }

    onSetFilterState(filterState.map((x, i) => (i === prevStateIndex ? { ...x, options: nextOptions } : x)));
  };

  return (
    <div
      className=' h-full overflow-auto  px-3 py-5 lg_px-0'
      ref={node => {
        scrollContainerRef.current = node;
        setPopoverPortalContainer(node);
      }}
    >
      <div
        className=' relative flex h-full flex-wrap content-start gap-x-1.5 gap-y-[15px]'
        ref={node => {
          setContainer(node);
        }}
      >
        {colorsFamilyOptions?.map((colorsFamily, index) => {
          if (!colorsFamily) {
            return null;
          }

          const values = colorsFamily.value?.split(',');
          const subColorOptions = values?.reduce<AggregationRoot[]>((acc, cur) => {
            const aggregationValue = colorAggregationIdMap[cur];

            if (aggregationValue && aggregationValue.label && aggregationValue.option_id && aggregationValue.rewrite) {
              acc.push(aggregationValue);
            }

            return acc;
          }, []);

          if (!Array.isArray(subColorOptions) || subColorOptions.length === 0) {
            return null;
          }

          return (
            <FilterDialogColorsFamilyFilterItem
              key={index}
              swatchData={colorsFamily.swatch_data || null}
              colorsFamilyLabel={colorsFamily.label || ''}
              subColorOptions={subColorOptions}
              selectedColorRewriteMap={selectedColorRewriteMap}
              onSelect={handleSelect}
              collisionBoundary={container}
              popoverPortalContainer={popoverPortalContainer}
              onOpenPopover={(e, popoverContentId) => {
                handleOpenPopover(e, index, popoverContentId);
              }}
              onClosePopover={() => {
                handleClosePopover(index);
              }}
              popoverVisible={visiblePopoverIndex === index}
            />
          );
        })}
      </div>
    </div>
  );
};

const FilterDialogColorsFamilyFilterItem = ({
  subColorOptions,
  selectedColorRewriteMap,
  onSelect,
  collisionBoundary,
  popoverPortalContainer,
  colorsFamilyLabel,
  swatchData,
  popoverVisible,
  onOpenPopover,
  onClosePopover,
}: {
  subColorOptions: {
    code: string;
    option_id: string;
    rewrite: string;
    label?: string;
    swatch_data: {
      value: string;
      thumbnail?: string;
    };
  }[];
  selectedColorRewriteMap: Record<string, true>;
  onSelect: (filterItems: { value: string; label: string; rewrite: string }[], prevActive: boolean) => void;
  collisionBoundary: HTMLDivElement | null;
  popoverPortalContainer: HTMLDivElement | null;
  colorsFamilyLabel: string;
  swatchData: null | {
    value?: string;
    thumbnail?: string;
  };
  popoverVisible: boolean;
  onOpenPopover: (e: React.MouseEvent<HTMLElement, MouseEvent>, popoverContentId: string) => void;
  onClosePopover: () => void;
}) => {
  const selectedCount = subColorOptions.reduce((acc, cur) => {
    return acc + (selectedColorRewriteMap[cur.rewrite] ? 1 : 0);
  }, 0);
  const isSelectedAll = subColorOptions.length === selectedCount;
  const curColorFamilyHasSelected = isSelectedAll || selectedCount > 0;
  const colorsFamilyRewrite = colorsFamilyLabel.toLowerCase().replace(/\s/g, '-') || '';
  const isColorAll = colorsFamilyRewrite === 'all';
  const colorRectStyle = createColorStyle(swatchData?.thumbnail, swatchData?.value);
  const popoverId = `filter-dialog-colors-family-${colorsFamilyLabel}-popover-content`;

  return (
    <Popover
      open={popoverVisible}
      modal
    >
      <PopoverAnchor asChild>
        <button
          className='flex w-[50px] flex-none flex-col'
          type='button'
          onClick={e => {
            onOpenPopover(e, popoverId);
          }}
          data-popover-trigger='1'
        >
          <div
            className={cn(
              'cursor-pointer rounded-[5px] border-1 border-solid border-80583F ',
              curColorFamilyHasSelected ? 'border-80583F' : 'border-transparent',
              'mx-auto flex h-[46px] w-[46px] items-center justify-center',
            )}
          >
            <div
              style={isColorAll ? undefined : colorRectStyle}
              className={cn(
                '  h-10 w-10 rounded-[5px] bg-contain',
                isWhite(swatchData?.value || '') ? 'border-1 border-solid border-E7DED5' : '',
              )}
            >
              {isColorAll ? (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='67'
                  height='66'
                  fill='none'
                  viewBox='0 0 67 66'
                  className='h-auto w-full'
                >
                  <g clipPath='url(#clip0_13530_35876)'>
                    <path
                      fill='#EFEFE6'
                      d='M.5 0h22v22H.5z'
                    />
                    <path
                      fill='#E0D1BD'
                      d='M22.5 0h22v22h-22z'
                    />
                    <path
                      fill='#D68B59'
                      d='M44.5 0h22v22h-22z'
                    />
                    <path
                      fill='#F0D3C9'
                      d='M.5 22h22v22H.5z'
                    />
                    <path
                      fill='#CC8986'
                      d='M22.5 22h22v22h-22z'
                    />
                    <path
                      fill='#9A1834'
                      d='M44.5 22h22v22h-22z'
                    />
                    <path
                      fill='#C4C3D0'
                      d='M.5 44h22v22H.5z'
                    />
                    <path
                      fill='#6F6E71'
                      d='M22.5 44h22v22h-22z'
                    />
                    <path
                      fill='#002712'
                      d='M44.5 44h22v22h-22z'
                    />
                  </g>
                  <defs>
                    <clipPath id='clip0_13530_35876'>
                      <rect
                        width='66'
                        height='66'
                        x='0.5'
                        fill='#fff'
                        rx='5'
                      />
                    </clipPath>
                  </defs>
                </svg>
              ) : null}
            </div>
          </div>
          <div className={clsx(' w-full text-center text-[13px] leading-[18px]')}>
            {colorsFamilyLabel}
            {selectedCount > 0 ? (
              <>
                <br />({selectedCount})
              </>
            ) : null}
          </div>
        </button>
      </PopoverAnchor>
      <PopoverContent
        className='z-[400] rounded-[10px] bg-white shadow-[0_0_10px_0_rgba(0,0,0,0.1)]'
        side='bottom'
        portalContainer={popoverPortalContainer}
        onPointerDownOutside={e => {
          const eventTarget = e.target;

          if (eventTarget instanceof Element) {
            if (eventTarget instanceof HTMLButtonElement && eventTarget.dataset.popoverTrigger === '1') {
              return;
            }

            let parentNode = eventTarget.parentElement;

            while (parentNode) {
              if (parentNode === popoverPortalContainer || parentNode === document.documentElement) {
                break;
              }

              if (parentNode instanceof HTMLButtonElement && parentNode.dataset.popoverTrigger === '1') {
                return;
              }

              parentNode = parentNode.parentElement;
            }
          }

          onClosePopover();
        }}
        collisionBoundary={collisionBoundary}
        id={popoverId}
      >
        <div
          className={clsx(
            'max-h-[320px]',
            'list-none gap-x-[10px] gap-y-6 overflow-y-auto overscroll-none ',
            'px-2.5 py-4',
          )}
        >
          {!isColorAll && (
            <div className='mb-[15px] pl-2.5'>
              <button
                className='flex items-center gap-3'
                type='button'
                onClick={() => {
                  onSelect(
                    subColorOptions.map(x => ({ label: x.label || '', value: x.option_id, rewrite: x.rewrite })),
                    isSelectedAll,
                  );
                }}
              >
                <span
                  className={clsx(
                    'h-5 w-5',
                    'border-1 border-solid border-80583F',
                    isSelectedAll ? 'bg-80583F' : 'bg-transparent',
                    'flex items-center justify-center',
                  )}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='15'
                    height='13'
                    fill='none'
                    viewBox='0 0 15 13'
                    className={clsx(!isSelectedAll && 'hidden')}
                  >
                    <path
                      stroke='#fff'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='1.5'
                      d='m13.5 1.305-8.4 10-3.6-3.75'
                    />
                  </svg>
                </span>
                <span className='text-sm leading-5 text-#403732'>SELECT ALL</span>
              </button>
            </div>
          )}
          <div
            className={clsx(
              subColorOptions.length > 3 ? 'grid grid-cols-3 lg_grid-cols-5' : 'flex flex-wrap',
              ' gap-2.5',
            )}
          >
            {subColorOptions.map((colorOption: any, index: number) => {
              if (!colorOption) {
                return null;
              }

              const colorRewrite = colorOption.rewrite;
              const swatch_data = colorOption.swatch_data;
              const isSelected = isSelectedAll || selectedColorRewriteMap[colorRewrite];
              const colorRectStyle = createColorStyle(swatch_data?.thumbnail, swatch_data?.value);

              return (
                <button
                  key={index}
                  className='group flex cursor-pointer flex-col items-center justify-start gap-[10px]  '
                  type='button'
                  onClick={() => {
                    onSelect(
                      [
                        {
                          label: colorOption.label,
                          rewrite: colorOption.rewrite,
                          value: colorOption.option_id,
                        },
                      ],
                      isSelected,
                    );
                  }}
                >
                  <div
                    className={cn(
                      ' rounded-[50%] border-2 border-solid lg_group-hover_border-80583F',
                      isSelected ? 'border-80583F' : 'border-transparent',
                    )}
                  >
                    <div
                      style={colorRectStyle}
                      className={cn(
                        'h-10 w-10 rounded-full bg-contain bg-clip-content p-[3px] ',
                        isWhite(swatch_data?.value || '') ? 'border-1 border-E7DED5' : '',
                      )}
                    />
                  </div>

                  <span
                    className={clsx(
                      ' text-center text-[13px] leading-5 text-#403732 lg_group-hover_text-80583F ',
                      'w-[60px]',
                    )}
                  >
                    {websiteEnv.isUnitedKingdom && colorOption?.label === 'Multi Color'
                      ? 'Multicolour'
                      : colorOption?.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
        <PopoverArrow className='h-auto w-4 fill-white ' />
      </PopoverContent>
    </Popover>
  );
};

const isWhite = (value: string) => {
  const lowercaseValue = value.toLowerCase();

  return lowercaseValue === '#fff' || lowercaseValue === '#ffffff';
};

const createColorStyle = (thumbnail: string | undefined, value: string | undefined) => {
  if (thumbnail && thumbnail.indexOf('#') < 0) {
    const imagePath = generateUrl(thumbnail, 'image-swatch')(48);

    return {
      backgroundImage: `url("${imagePath}")`,
    };
  }

  return {
    backgroundColor: value,
  };
};

export default FilterDialogColorsFamilyFilter;
